import React, { useState, useEffect } from 'react';
import { motion } from 'motion/react';
import { 
  FileText, Users, FolderOpen, Tag, Layers, BarChart3, 
  Plus, Eye, Edit, Trash2, Clock, CheckCircle, Archive,
  Lock, LogOut, RefreshCw, Search, Filter, Calendar,
  TrendingUp, Sparkles, Target, MapPin, Share2, Code, Rocket,
  Zap, Power, Play, Activity
} from 'lucide-react';
import { toast } from 'sonner';
import BlogEditor from './BlogEditor';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const iconMap = {
  TrendingUp, Sparkles, Target, MapPin, Share2, Code, Rocket, FileText
};

const statusColors = {
  draft: 'bg-yellow-100 text-yellow-700 border-yellow-200',
  scheduled: 'bg-amber-100 text-amber-700 border-amber-200',
  published: 'bg-green-100 text-green-700 border-green-200',
  archived: 'bg-gray-100 text-gray-700 border-gray-200'
};

// PIN Login Component
const CMSLogin = ({ onSuccess }) => {
  const [pin, setPin] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/api/cms/verify-pin`, {
        method: 'POST',
        headers: { 'X-CMS-Pin': pin }
      });
      
      const data = await response.json();
      
      if (data.valid) {
        sessionStorage.setItem('cmsPin', pin);
        onSuccess(pin);
        toast.success('Welcome to CMS Dashboard!');
      } else {
        setError('Invalid PIN. Please try again.');
        setPin('');
      }
    } catch (err) {
      setError('Connection error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md mx-4"
      >
        <div className="bg-slate-800/50 backdrop-blur-xl rounded-2xl shadow-2xl p-8 border border-slate-700">
          <div className="text-center mb-8">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-orange-500 to-amber-500 flex items-center justify-center mx-auto mb-4">
              <FileText className="text-white" size={32} />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">Blog CMS</h1>
            <p className="text-slate-400 text-sm">Enter your PIN to access the dashboard</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-slate-400 text-sm mb-2">CMS Admin PIN</label>
              <input
                type="password"
                value={pin}
                onChange={(e) => setPin(e.target.value)}
                placeholder="Enter PIN"
                className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-xl text-white text-center text-2xl tracking-[0.5em] font-mono focus:outline-none focus:border-orange-500"
                autoFocus
                data-testid="cms-pin-input"
              />
            </div>

            {error && (
              <p className="text-red-400 text-sm text-center">{error}</p>
            )}

            <motion.button
              type="submit"
              disabled={loading || pin.length < 4}
              className="w-full py-4 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-xl font-semibold disabled:opacity-50 hover:shadow-lg hover:shadow-orange-500/20 transition-shadow"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              data-testid="cms-login-btn"
            >
              {loading ? 'Verifying...' : 'Access CMS Dashboard'}
            </motion.button>
          </form>
        </div>
      </motion.div>
    </div>
  );
};

// Sidebar Navigation
const Sidebar = ({ activeTab, setActiveTab, onLogout }) => {
  const navItems = [
    { id: 'dashboard', label: 'Dashboard', icon: BarChart3 },
    { id: 'blogs', label: 'Blog Posts', icon: FileText },
    { id: 'authors', label: 'Authors', icon: Users },
    { id: 'categories', label: 'Categories', icon: FolderOpen },
    { id: 'tags', label: 'Tags', icon: Tag },
    { id: 'clusters', label: 'Topic Clusters', icon: Layers },
    { id: 'engine', label: 'Content Engine', icon: Zap },
    { id: 'analytics', label: 'Analytics', icon: TrendingUp },
  ];

  return (
    <div className="w-64 bg-slate-800 min-h-screen p-4 flex flex-col">
      <div className="flex items-center gap-3 mb-8 px-2">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-500 to-amber-500 flex items-center justify-center">
          <FileText className="text-white" size={20} />
        </div>
        <div>
          <h1 className="text-white font-bold">Blog CMS</h1>
          <p className="text-slate-400 text-xs">Buzz Craft Maven</p>
        </div>
      </div>

      <nav className="flex-1 space-y-1">
        {navItems.map((item) => (
          <button
            key={item.id}
            onClick={() => setActiveTab(item.id)}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
              activeTab === item.id
                ? 'bg-gradient-to-r from-orange-500/20 to-amber-500/20 text-orange-400 border border-orange-500/30'
                : 'text-slate-400 hover:bg-slate-700/50 hover:text-white'
            }`}
          >
            <item.icon size={20} />
            {item.label}
          </button>
        ))}
      </nav>

      <button
        onClick={onLogout}
        className="flex items-center gap-3 px-4 py-3 text-slate-400 hover:text-red-400 hover:bg-red-500/10 rounded-xl transition-all mt-4"
      >
        <LogOut size={20} />
        Logout
      </button>
    </div>
  );
};

// Dashboard Stats
const DashboardView = ({ stats, cmsPin }) => {
  const statCards = [
    { label: 'Total Blogs', value: stats.total_blogs, icon: FileText, color: 'orange' },
    { label: 'Published', value: stats.published_blogs, icon: CheckCircle, color: 'green' },
    { label: 'Drafts', value: stats.draft_blogs, icon: Edit, color: 'yellow' },
    { label: 'Scheduled', value: stats.scheduled_blogs, icon: Calendar, color: 'amber' },
    { label: 'Total Views', value: stats.total_views, icon: Eye, color: 'rose' },
    { label: 'Authors', value: stats.total_authors, icon: Users, color: 'orange' },
    { label: 'Categories', value: stats.total_categories, icon: FolderOpen, color: 'amber' },
    { label: 'Topic Clusters', value: stats.total_clusters, icon: Layers, color: 'yellow' },
  ];

  const colorClasses = {
    orange: 'from-orange-500/20 to-orange-600/20 border-orange-500/30 text-orange-400',
    green: 'from-green-500/20 to-green-600/20 border-green-500/30 text-green-400',
    yellow: 'from-yellow-500/20 to-yellow-600/20 border-yellow-500/30 text-yellow-400',
    amber: 'from-amber-500/20 to-amber-600/20 border-amber-500/30 text-amber-400',
    rose: 'from-rose-500/20 to-rose-600/20 border-rose-500/30 text-rose-400',
  };

  return (
    <div>
      <h2 className="text-2xl font-bold text-white mb-6">Dashboard Overview</h2>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((stat) => (
          <motion.div
            key={stat.label}
            className={`p-6 rounded-2xl bg-gradient-to-br ${colorClasses[stat.color]} border backdrop-blur-sm`}
            whileHover={{ scale: 1.02 }}
          >
            <div className="flex items-center justify-between mb-4">
              <stat.icon size={24} />
              <span className="text-3xl font-bold">{stat.value}</span>
            </div>
            <p className="text-slate-300 text-sm">{stat.label}</p>
          </motion.div>
        ))}
      </div>

      <div className="mt-8 p-6 bg-slate-800/50 rounded-2xl border border-slate-700">
        <h3 className="text-lg font-semibold text-white mb-4">Quick Actions</h3>
        <div className="flex flex-wrap gap-3">
          <button className="flex items-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors">
            <Plus size={18} />
            New Blog Post
          </button>
          <button className="flex items-center gap-2 px-4 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-600 transition-colors">
            <Users size={18} />
            Add Author
          </button>
          <button className="flex items-center gap-2 px-4 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-600 transition-colors">
            <FolderOpen size={18} />
            Add Category
          </button>
        </div>
      </div>
    </div>
  );
};

// Blog List View
const BlogsView = ({ cmsPin, onCreateNew }) => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');

  useEffect(() => {
    fetchBlogs();
  }, [filter]);

  const fetchBlogs = async () => {
    setLoading(true);
    try {
      const statusParam = filter !== 'all' ? `?status=${filter}` : '';
      const response = await fetch(`${API_URL}/api/cms/blogs${statusParam}`, {
        headers: { 'X-CMS-Pin': cmsPin }
      });
      const data = await response.json();
      setBlogs(data);
    } catch (error) {
      toast.error('Failed to load blogs');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (blogId) => {
    if (!window.confirm('Are you sure you want to delete this blog?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/cms/blogs/${blogId}`, {
        method: 'DELETE',
        headers: { 'X-CMS-Pin': cmsPin }
      });
      
      if (response.ok) {
        toast.success('Blog deleted successfully');
        fetchBlogs();
      } else {
        toast.error('Failed to delete blog');
      }
    } catch (error) {
      toast.error('Failed to delete blog');
    }
  };

  const handlePublish = async (blogId) => {
    try {
      const response = await fetch(`${API_URL}/api/cms/blogs/${blogId}/publish`, {
        method: 'POST',
        headers: { 'X-CMS-Pin': cmsPin }
      });
      
      if (response.ok) {
        toast.success('Blog published successfully');
        fetchBlogs();
      }
    } catch (error) {
      toast.error('Failed to publish blog');
    }
  };

  const filteredBlogs = blogs.filter(blog => 
    blog.title.toLowerCase().includes(search.toLowerCase()) ||
    blog.slug.toLowerCase().includes(search.toLowerCase())
  );

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-white">Blog Posts</h2>
        <button
          onClick={onCreateNew}
          className="flex items-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
        >
          <Plus size={18} />
          New Blog
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4 mb-6">
        <div className="relative flex-1 max-w-md">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            placeholder="Search blogs..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-orange-500"
          />
        </div>
        
        <div className="flex items-center gap-2">
          <Filter size={18} className="text-slate-400" />
          {['all', 'published', 'draft', 'scheduled'].map((status) => (
            <button
              key={status}
              onClick={() => setFilter(status)}
              className={`px-3 py-1.5 rounded-lg text-sm capitalize transition-colors ${
                filter === status
                  ? 'bg-orange-500 text-white'
                  : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
              }`}
            >
              {status}
            </button>
          ))}
        </div>

        <button
          onClick={fetchBlogs}
          className="p-2 bg-slate-700 text-slate-300 rounded-lg hover:bg-slate-600 transition-colors"
        >
          <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>

      {/* Blog List */}
      {loading ? (
        <div className="space-y-4">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="p-4 bg-slate-800/50 rounded-xl animate-pulse">
              <div className="h-5 bg-slate-700 rounded w-1/2 mb-2" />
              <div className="h-4 bg-slate-700 rounded w-1/3" />
            </div>
          ))}
        </div>
      ) : filteredBlogs.length === 0 ? (
        <div className="text-center py-12">
          <FileText size={48} className="mx-auto text-slate-600 mb-4" />
          <p className="text-slate-400">No blogs found</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredBlogs.map((blog) => (
            <motion.div
              key={blog.id}
              className="p-4 bg-slate-800/50 border border-slate-700 rounded-xl hover:border-slate-600 transition-colors"
              whileHover={{ x: 4 }}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-white font-medium">{blog.title}</h3>
                    <span className={`px-2 py-0.5 text-xs rounded-full border ${statusColors[blog.status]}`}>
                      {blog.status}
                    </span>
                    {blog.is_featured && (
                      <span className="px-2 py-0.5 text-xs rounded-full bg-amber-500/20 text-amber-400 border border-amber-500/30">
                        Featured
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-4 text-sm text-slate-400">
                    <span className="flex items-center gap-1">
                      <FolderOpen size={14} />
                      {blog.category}
                    </span>
                    <span className="flex items-center gap-1">
                      <Users size={14} />
                      {blog.author_name}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock size={14} />
                      {formatDate(blog.published_at || blog.created_at)}
                    </span>
                    <span className="flex items-center gap-1">
                      <Eye size={14} />
                      {blog.views} views
                    </span>
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  {blog.status === 'draft' && (
                    <button
                      onClick={() => handlePublish(blog.id)}
                      className="p-2 text-green-400 hover:bg-green-500/10 rounded-lg transition-colors"
                      title="Publish"
                    >
                      <CheckCircle size={18} />
                    </button>
                  )}
                  <button
                    onClick={() => window.open(`/blog/${blog.slug}`, '_blank')}
                    className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg transition-colors"
                    title="View"
                  >
                    <Eye size={18} />
                  </button>
                  <button
                    onClick={() => onCreateNew(blog)}
                    className="p-2 text-slate-400 hover:text-orange-400 hover:bg-orange-500/10 rounded-lg transition-colors"
                    title="Edit"
                  >
                    <Edit size={18} />
                  </button>
                  <button
                    onClick={() => handleDelete(blog.id)}
                    className="p-2 text-slate-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                    title="Delete"
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
};

// Authors View
const AuthorsView = ({ cmsPin }) => {
  const [authors, setAuthors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingAuthor, setEditingAuthor] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    bio: '',
    profile_image: '',
    linkedin_url: '',
    expertise: []
  });

  useEffect(() => {
    fetchAuthors();
  }, []);

  const fetchAuthors = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/cms/authors`, {
        headers: { 'X-CMS-Pin': cmsPin }
      });
      const data = await response.json();
      setAuthors(data);
    } catch (error) {
      toast.error('Failed to load authors');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const url = editingAuthor 
      ? `${API_URL}/api/cms/authors/${editingAuthor.id}`
      : `${API_URL}/api/cms/authors`;
    
    const method = editingAuthor ? 'PUT' : 'POST';
    
    try {
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'X-CMS-Pin': cmsPin
        },
        body: JSON.stringify(formData)
      });
      
      if (response.ok) {
        toast.success(editingAuthor ? 'Author updated!' : 'Author created!');
        setShowForm(false);
        setEditingAuthor(null);
        setFormData({ name: '', slug: '', bio: '', profile_image: '', linkedin_url: '', expertise: [] });
        fetchAuthors();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save author');
      }
    } catch (error) {
      toast.error('Failed to save author');
    }
  };

  const handleEdit = (author) => {
    setEditingAuthor(author);
    setFormData({
      name: author.name,
      slug: author.slug,
      bio: author.bio,
      profile_image: author.profile_image || '',
      linkedin_url: author.linkedin_url || '',
      expertise: author.expertise || []
    });
    setShowForm(true);
  };

  const handleDelete = async (authorId) => {
    if (!window.confirm('Are you sure you want to delete this author?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/cms/authors/${authorId}`, {
        method: 'DELETE',
        headers: { 'X-CMS-Pin': cmsPin }
      });
      
      if (response.ok) {
        toast.success('Author deleted');
        fetchAuthors();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to delete author');
      }
    } catch (error) {
      toast.error('Failed to delete author');
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-white">Authors</h2>
        <button
          onClick={() => {
            setEditingAuthor(null);
            setFormData({ name: '', slug: '', bio: '', profile_image: '', linkedin_url: '', expertise: [] });
            setShowForm(true);
          }}
          className="flex items-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
        >
          <Plus size={18} />
          Add Author
        </button>
      </div>

      {showForm && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6 p-6 bg-slate-800/50 border border-slate-700 rounded-xl"
        >
          <h3 className="text-lg font-semibold text-white mb-4">
            {editingAuthor ? 'Edit Author' : 'New Author'}
          </h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-slate-400 text-sm mb-1">Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-orange-500"
                  required
                />
              </div>
              <div>
                <label className="block text-slate-400 text-sm mb-1">Slug</label>
                <input
                  type="text"
                  value={formData.slug}
                  onChange={(e) => setFormData({...formData, slug: e.target.value.toLowerCase().replace(/\s+/g, '-')})}
                  className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-orange-500"
                  required
                />
              </div>
            </div>
            <div>
              <label className="block text-slate-400 text-sm mb-1">Bio</label>
              <textarea
                value={formData.bio}
                onChange={(e) => setFormData({...formData, bio: e.target.value})}
                rows={3}
                className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-orange-500 resize-none"
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-slate-400 text-sm mb-1">LinkedIn URL</label>
                <input
                  type="url"
                  value={formData.linkedin_url}
                  onChange={(e) => setFormData({...formData, linkedin_url: e.target.value})}
                  className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-orange-500"
                />
              </div>
              <div>
                <label className="block text-slate-400 text-sm mb-1">Expertise (comma separated)</label>
                <input
                  type="text"
                  value={formData.expertise.join(', ')}
                  onChange={(e) => setFormData({...formData, expertise: e.target.value.split(',').map(s => s.trim()).filter(Boolean)})}
                  className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-orange-500"
                />
              </div>
            </div>
            <div className="flex gap-3">
              <button
                type="submit"
                className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
              >
                {editingAuthor ? 'Update' : 'Create'} Author
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowForm(false);
                  setEditingAuthor(null);
                }}
                className="px-4 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-600 transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
        </motion.div>
      )}

      {loading ? (
        <div className="space-y-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="p-4 bg-slate-800/50 rounded-xl animate-pulse">
              <div className="h-5 bg-slate-700 rounded w-1/4 mb-2" />
              <div className="h-4 bg-slate-700 rounded w-1/2" />
            </div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {authors.map((author) => (
            <motion.div
              key={author.id}
              className="p-4 bg-slate-800/50 border border-slate-700 rounded-xl"
              whileHover={{ scale: 1.01 }}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-orange-500 to-amber-500 flex items-center justify-center text-white font-bold">
                    {author.name.charAt(0)}
                  </div>
                  <div>
                    <h3 className="text-white font-medium">{author.name}</h3>
                    <p className="text-slate-400 text-sm">{author.articles_count} articles</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleEdit(author)}
                    className="p-2 text-slate-400 hover:text-orange-400 rounded-lg transition-colors"
                  >
                    <Edit size={16} />
                  </button>
                  <button
                    onClick={() => handleDelete(author.id)}
                    className="p-2 text-slate-400 hover:text-red-400 rounded-lg transition-colors"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
              <p className="text-slate-400 text-sm mt-3 line-clamp-2">{author.bio}</p>
              {author.expertise.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-3">
                  {author.expertise.map((exp, i) => (
                    <span key={i} className="px-2 py-0.5 text-xs bg-slate-700 text-slate-300 rounded">
                      {exp}
                    </span>
                  ))}
                </div>
              )}
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
};

// Categories View
const CategoriesView = ({ cmsPin }) => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ name: '', slug: '', description: '', icon: '' });

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/cms/categories`, {
        headers: { 'X-CMS-Pin': cmsPin }
      });
      const data = await response.json();
      setCategories(data);
    } catch (error) {
      toast.error('Failed to load categories');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${API_URL}/api/cms/categories`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CMS-Pin': cmsPin
        },
        body: JSON.stringify(formData)
      });
      
      if (response.ok) {
        toast.success('Category created!');
        setShowForm(false);
        setFormData({ name: '', slug: '', description: '', icon: '' });
        fetchCategories();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create category');
      }
    } catch (error) {
      toast.error('Failed to create category');
    }
  };

  const handleDelete = async (categoryId) => {
    if (!window.confirm('Are you sure you want to delete this category?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/cms/categories/${categoryId}`, {
        method: 'DELETE',
        headers: { 'X-CMS-Pin': cmsPin }
      });
      
      if (response.ok) {
        toast.success('Category deleted');
        fetchCategories();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to delete category');
      }
    } catch (error) {
      toast.error('Failed to delete category');
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-white">Categories</h2>
        <button
          onClick={() => setShowForm(true)}
          className="flex items-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
        >
          <Plus size={18} />
          Add Category
        </button>
      </div>

      {showForm && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6 p-6 bg-slate-800/50 border border-slate-700 rounded-xl"
        >
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-slate-400 text-sm mb-1">Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-orange-500"
                  required
                />
              </div>
              <div>
                <label className="block text-slate-400 text-sm mb-1">Slug</label>
                <input
                  type="text"
                  value={formData.slug}
                  onChange={(e) => setFormData({...formData, slug: e.target.value.toLowerCase().replace(/\s+/g, '-')})}
                  className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-orange-500"
                  required
                />
              </div>
            </div>
            <div>
              <label className="block text-slate-400 text-sm mb-1">Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                rows={2}
                className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:outline-none focus:border-orange-500 resize-none"
                required
              />
            </div>
            <div className="flex gap-3">
              <button type="submit" className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors">
                Create Category
              </button>
              <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-600 transition-colors">
                Cancel
              </button>
            </div>
          </form>
        </motion.div>
      )}

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="p-4 bg-slate-800/50 rounded-xl animate-pulse">
              <div className="h-5 bg-slate-700 rounded w-1/2 mb-2" />
              <div className="h-4 bg-slate-700 rounded w-full" />
            </div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {categories.map((category) => {
            const IconComponent = iconMap[category.icon] || FolderOpen;
            return (
              <motion.div
                key={category.id}
                className="p-4 bg-slate-800/50 border border-slate-700 rounded-xl"
                whileHover={{ scale: 1.02 }}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="w-10 h-10 rounded-lg bg-orange-500/20 flex items-center justify-center">
                    <IconComponent size={20} className="text-orange-400" />
                  </div>
                  <button
                    onClick={() => handleDelete(category.id)}
                    className="p-1 text-slate-400 hover:text-red-400 transition-colors"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
                <h3 className="text-white font-medium mb-1">{category.name}</h3>
                <p className="text-slate-400 text-sm mb-2 line-clamp-2">{category.description}</p>
                <span className="text-xs text-slate-500">{category.post_count} posts</span>
              </motion.div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// Tags View
const TagsView = ({ cmsPin }) => {
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTags();
  }, []);

  const fetchTags = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/cms/tags`, {
        headers: { 'X-CMS-Pin': cmsPin }
      });
      const data = await response.json();
      setTags(data);
    } catch (error) {
      toast.error('Failed to load tags');
    } finally {
      setLoading(false);
    }
  };

  const maxCount = Math.max(...tags.map(t => t.post_count || 1), 1);

  return (
    <div>
      <h2 className="text-2xl font-bold text-white mb-2">Tags</h2>
      <p className="text-slate-400 text-sm mb-6">Tags are auto-generated from published blog posts. Larger tags have more posts.</p>

      {loading ? (
        <div className="flex flex-wrap gap-2">
          {[...Array(20)].map((_, i) => (
            <div key={i} className="h-8 w-24 bg-slate-700 rounded-lg animate-pulse" />
          ))}
        </div>
      ) : tags.length === 0 ? (
        <div className="text-center py-12">
          <Tag size={48} className="mx-auto text-slate-600 mb-4" />
          <p className="text-slate-400">No tags yet. Tags are created when you add them to blog posts.</p>
        </div>
      ) : (
        <div>
          <div className="flex flex-wrap gap-3">
            {tags.map((tag, idx) => {
              const scale = 0.7 + (tag.post_count / maxCount) * 0.6;
              return (
                <motion.div
                  key={`${tag.id}-${idx}`}
                  className="px-4 py-2 bg-slate-800/50 border border-slate-700 rounded-xl hover:border-orange-500/30 hover:bg-orange-500/5 transition-all cursor-default"
                  whileHover={{ scale: 1.05 }}
                  style={{ fontSize: `${scale}rem` }}
                >
                  <span className="text-orange-400 font-medium">#{tag.name}</span>
                  <span className="text-slate-500 ml-2 text-xs">({tag.post_count})</span>
                </motion.div>
              );
            })}
          </div>

          <div className="mt-8 bg-slate-800/50 rounded-xl p-5 border border-slate-700">
            <h3 className="text-white font-medium mb-4">Tag Distribution</h3>
            <div className="space-y-2">
              {tags.slice(0, 10).map((tag, idx) => (
                <div key={`dist-${tag.id}-${idx}`} className="flex items-center gap-3">
                  <span className="text-slate-300 text-sm w-32 truncate">{tag.name}</span>
                  <div className="flex-1 h-2 bg-slate-700 rounded-full overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${(tag.post_count / maxCount) * 100}%` }}
                      transition={{ duration: 0.6 }}
                      className="h-full bg-gradient-to-r from-orange-500 to-amber-500 rounded-full"
                    />
                  </div>
                  <span className="text-slate-500 text-xs w-8 text-right">{tag.post_count}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Clusters View
const ClustersView = ({ cmsPin }) => {
  const [clusters, setClusters] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchClusters();
  }, []);

  const fetchClusters = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/cms/clusters`, {
        headers: { 'X-CMS-Pin': cmsPin }
      });
      const data = await response.json();
      setClusters(data);
    } catch (error) {
      toast.error('Failed to load clusters');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <h2 className="text-2xl font-bold text-white mb-6">Topic Clusters</h2>
      <p className="text-slate-400 text-sm mb-6">Topic clusters group related blogs together for better SEO and content organization. Blogs are auto-assigned based on their category.</p>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="p-4 bg-slate-800/50 rounded-xl animate-pulse">
              <div className="h-5 bg-slate-700 rounded w-1/2 mb-2" />
              <div className="h-4 bg-slate-700 rounded w-full" />
            </div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {clusters.map((cluster) => (
            <motion.div
              key={cluster.id}
              className="p-5 bg-slate-800/50 border border-slate-700 rounded-xl"
              whileHover={{ scale: 1.01 }}
            >
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <Layers size={20} className="text-orange-400" />
                  <h3 className="text-white font-medium">{cluster.name}</h3>
                </div>
                <span className="text-xs px-2 py-1 bg-orange-500/20 text-orange-400 rounded-full font-medium">
                  {cluster.blog_count || 0} blogs
                </span>
              </div>
              <p className="text-slate-400 text-sm mb-3">{cluster.description}</p>
              {cluster.blogs?.length > 0 && (
                <div className="space-y-1.5 mt-3 pt-3 border-t border-slate-700">
                  {cluster.blogs.slice(0, 5).map((blog) => (
                    <div key={blog.id} className="flex items-center justify-between text-sm">
                      <span className="text-slate-300 truncate flex-1 mr-2">{blog.title}</span>
                      <span className="text-slate-500 text-xs flex-shrink-0">{blog.views || 0} views</span>
                    </div>
                  ))}
                  {cluster.blog_count > 5 && (
                    <p className="text-xs text-slate-500 mt-1">+{cluster.blog_count - 5} more</p>
                  )}
                </div>
              )}
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
};

// Content Engine View
const ContentEngineView = ({ cmsPin }) => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [checkingTrends, setCheckingTrends] = useState(false);
  const [selectedSnippets, setSelectedSnippets] = useState(null);
  const [snippetsLoading, setSnippetsLoading] = useState(false);
  const [pendingTrends, setPendingTrends] = useState([]);
  const [publishingTrend, setPublishingTrend] = useState(null);
  const [autoBlogs, setAutoBlogs] = useState([]);

  const fetchStatus = async () => {
    try {
      const res = await fetch(`${API_URL}/api/content-engine/status`, {
        headers: { 'X-CMS-Pin': cmsPin }
      });
      const data = await res.json();
      setStatus(data);
    } catch (err) {
      toast.error('Failed to fetch engine status');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchStatus(); fetchPendingTrends(); fetchAutoBlogs(); }, []);

  const fetchPendingTrends = async () => {
    try {
      const res = await fetch(`${API_URL}/api/content-engine/pending-trends`, {
        headers: { 'X-CMS-Pin': cmsPin }
      });
      const data = await res.json();
      setPendingTrends(data.pending_trends || []);
    } catch (err) { /* ignore */ }
  };

  const fetchAutoBlogs = async () => {
    try {
      const res = await fetch(`${API_URL}/api/content-engine/auto-blogs?limit=20`, {
        headers: { 'X-CMS-Pin': cmsPin }
      });
      const data = await res.json();
      setAutoBlogs(data.blogs || []);
    } catch (err) { /* ignore */ }
  };

  const fetchSocialSnippets = async (blogId, blogTitle) => {
    setSnippetsLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/content-engine/social-snippets/${blogId}`, {
        headers: { 'X-CMS-Pin': cmsPin }
      });
      const data = await res.json();
      setSelectedSnippets({ ...data, title: blogTitle });
    } catch (err) {
      toast.error('Failed to load social snippets');
    } finally {
      setSnippetsLoading(false);
    }
  };

  const copyToClipboard = (text, platform) => {
    navigator.clipboard.writeText(text).then(() => {
      toast.success(`${platform} snippet copied!`);
    }).catch(() => {
      toast.error('Failed to copy');
    });
  };

  const handleToggle = async () => {
    setToggling(true);
    try {
      const res = await fetch(`${API_URL}/api/content-engine/toggle`, {
        method: 'POST',
        headers: { 'X-CMS-Pin': cmsPin }
      });
      const data = await res.json();
      toast.success(data.message);
      fetchStatus();
    } catch (err) {
      toast.error('Failed to toggle engine');
    } finally {
      setToggling(false);
    }
  };

  const handleGenerateNow = async () => {
    setGenerating(true);
    toast.info('Generating blog... This may take a minute.');
    try {
      const res = await fetch(`${API_URL}/api/content-engine/generate-now`, {
        method: 'POST',
        headers: { 'X-CMS-Pin': cmsPin }
      });
      const data = await res.json();
      if (data.success) {
        toast.success(`Published: "${data.blog.title}"`);
        fetchStatus();
      } else {
        toast.error(data.detail || 'Generation failed');
      }
    } catch (err) {
      toast.error('Failed to generate blog');
    } finally {
      setGenerating(false);
    }
  };

  const handleCheckTrends = async () => {
    setCheckingTrends(true);
    toast.info('Scanning for trending topics...');
    try {
      const res = await fetch(`${API_URL}/api/content-engine/check-trends`, {
        method: 'POST',
        headers: { 'X-CMS-Pin': cmsPin }
      });
      const data = await res.json();
      if (data.trending) {
        toast.success(`Trend detected: "${data.trend.topic}" — Review below to publish.`);
        fetchPendingTrends();
      } else {
        toast.info(data.message || 'No urgent trends detected');
      }
    } catch (err) {
      toast.error('Trend check failed');
    } finally {
      setCheckingTrends(false);
    }
  };

  const handlePublishTrend = async (trendId) => {
    setPublishingTrend(trendId);
    toast.info('Generating and publishing trending blog...');
    try {
      const res = await fetch(`${API_URL}/api/content-engine/publish-trend/${trendId}`, {
        method: 'POST',
        headers: { 'X-CMS-Pin': cmsPin }
      });
      const data = await res.json();
      if (data.success) {
        toast.success(`Published: "${data.blog.title}"`);
        fetchPendingTrends();
        fetchStatus();
        fetchAutoBlogs();
      } else {
        toast.error(data.detail || 'Failed to publish');
      }
    } catch (err) {
      toast.error('Failed to publish trending blog');
    } finally {
      setPublishingTrend(null);
    }
  };

  const handleDismissTrend = async (trendId) => {
    try {
      await fetch(`${API_URL}/api/content-engine/dismiss-trend/${trendId}`, {
        method: 'POST',
        headers: { 'X-CMS-Pin': cmsPin }
      });
      toast.info('Trend dismissed');
      fetchPendingTrends();
    } catch (err) {
      toast.error('Failed to dismiss');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><RefreshCw className="animate-spin text-orange-400" size={32} /></div>;
  }

  return (
    <div data-testid="content-engine-view">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-3">
            <Zap className="text-amber-400" size={28} /> Autonomous Content Engine
          </h2>
          <p className="text-slate-400 mt-1">Self-running content machine. Create. Adapt. Publish. Repeat.</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={handleToggle}
            disabled={toggling}
            data-testid="engine-toggle-btn"
            className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-medium transition-all ${
              status?.enabled
                ? 'bg-red-500/20 text-red-400 border border-red-500/30 hover:bg-red-500/30'
                : 'bg-green-500/20 text-green-400 border border-green-500/30 hover:bg-green-500/30'
            }`}
          >
            <Power size={18} /> {toggling ? 'Toggling...' : status?.enabled ? 'Disable Engine' : 'Enable Engine'}
          </button>
          <button
            onClick={handleCheckTrends}
            disabled={checkingTrends}
            data-testid="check-trends-btn"
            className="flex items-center gap-2 px-4 py-2.5 bg-amber-500/20 text-amber-400 border border-amber-500/30 rounded-xl font-medium hover:bg-amber-500/30 transition-all disabled:opacity-50"
          >
            <TrendingUp size={18} /> {checkingTrends ? 'Scanning...' : 'Check Trends'}
          </button>
          <button
            onClick={handleGenerateNow}
            disabled={generating}
            data-testid="generate-now-btn"
            className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-xl font-medium hover:shadow-lg hover:shadow-orange-500/20 transition-all disabled:opacity-50"
          >
            <Play size={18} /> {generating ? 'Generating...' : 'Generate & Publish Now'}
          </button>
        </div>
      </div>

      {/* Status Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <div className="bg-slate-800/50 rounded-xl p-5 border border-slate-700" data-testid="engine-status-card">
          <div className="flex items-center gap-3 mb-2">
            <Activity size={20} className={status?.enabled ? 'text-green-400' : 'text-red-400'} />
            <span className="text-slate-400 text-sm">Engine Status</span>
          </div>
          <p className={`text-2xl font-bold ${status?.enabled ? 'text-green-400' : 'text-red-400'}`}>
            {status?.enabled ? 'ACTIVE' : 'DISABLED'}
          </p>
        </div>
        <div className="bg-slate-800/50 rounded-xl p-5 border border-slate-700">
          <div className="flex items-center gap-3 mb-2">
            <FileText size={20} className="text-orange-400" />
            <span className="text-slate-400 text-sm">Auto Blogs</span>
          </div>
          <p className="text-2xl font-bold text-white">{status?.stats?.published_auto_blogs || 0}</p>
        </div>
        <div className="bg-slate-800/50 rounded-xl p-5 border border-slate-700">
          <div className="flex items-center gap-3 mb-2">
            <Eye size={20} className="text-orange-400" />
            <span className="text-slate-400 text-sm">Total Views</span>
          </div>
          <p className="text-2xl font-bold text-white">{status?.stats?.total_views || 0}</p>
        </div>
        <div className="bg-slate-800/50 rounded-xl p-5 border border-slate-700">
          <div className="flex items-center gap-3 mb-2">
            <Clock size={20} className="text-amber-400" />
            <span className="text-slate-400 text-sm">Next Auto-Publish</span>
          </div>
          <p className="text-sm font-medium text-white">{status?.next_run ? formatDate(status.next_run) : 'Not scheduled'}</p>
        </div>
      </div>

      {/* Pending Trends for Review */}
      {pendingTrends.length > 0 && (
        <div className="mb-8 bg-slate-800/50 rounded-xl p-6 border border-amber-500/30" data-testid="pending-trends">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <TrendingUp size={20} className="text-amber-400" /> Trending Topics — Review & Publish
          </h3>
          <div className="space-y-3">
            {pendingTrends.map((trend) => (
              <div key={trend.id} className="flex items-start gap-4 p-4 bg-slate-700/30 rounded-xl border border-slate-600/50" data-testid={`trend-${trend.id}`}>
                <div className="flex-1">
                  <p className="text-white font-medium">{trend.topic}</p>
                  <p className="text-slate-400 text-sm mt-1">{trend.reason}</p>
                  <div className="flex items-center gap-3 mt-2">
                    <span className="text-xs px-2 py-0.5 bg-amber-500/20 text-amber-400 rounded">{trend.category?.replace(/-/g, ' ')}</span>
                    <span className={`text-xs px-2 py-0.5 rounded ${trend.urgency === 'high' ? 'bg-red-500/20 text-red-400' : 'bg-yellow-500/20 text-yellow-400'}`}>
                      {trend.urgency} urgency
                    </span>
                    <span className="text-xs text-slate-500">{formatDate(trend.detected_at)}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button
                    onClick={() => handlePublishTrend(trend.id)}
                    disabled={publishingTrend === trend.id}
                    className="flex items-center gap-1.5 px-4 py-2 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-lg text-sm font-medium hover:shadow-lg transition-all disabled:opacity-50"
                    data-testid={`publish-trend-${trend.id}`}
                  >
                    <Play size={14} /> {publishingTrend === trend.id ? 'Publishing...' : 'Publish'}
                  </button>
                  <button
                    onClick={() => handleDismissTrend(trend.id)}
                    className="px-3 py-2 text-slate-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg text-sm transition-colors"
                  >
                    Dismiss
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Two columns: Top Blogs + Activity Log */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Performing Auto-Blogs */}
        <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <TrendingUp size={20} className="text-orange-400" /> Top Auto-Published Blogs
          </h3>
          {status?.top_blogs?.length > 0 ? (
            <div className="space-y-3">
              {status.top_blogs.map((blog, i) => (
                <div key={blog.id} className="flex items-center gap-3 p-3 bg-slate-700/30 rounded-lg" data-testid={`top-blog-${i}`}>
                  <span className="text-slate-500 font-bold text-sm w-6">#{i + 1}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-sm font-medium truncate">{blog.title}</p>
                    <p className="text-slate-400 text-xs mt-0.5">
                      {blog.category?.replace(/-/g, ' ')} &middot; {formatDate(blog.published_at)}
                    </p>
                  </div>
                  <button
                    onClick={() => fetchSocialSnippets(blog.id, blog.title)}
                    className="p-1.5 text-slate-400 hover:text-orange-400 hover:bg-orange-500/10 rounded-lg transition-colors"
                    title="View Social Snippets"
                    data-testid={`social-snippets-btn-${i}`}
                  >
                    <Share2 size={14} />
                  </button>
                  <div className="flex items-center gap-1 text-orange-400 text-sm">
                    <Eye size={14} /> {blog.views || 0}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-slate-500 text-sm">No auto-published blogs yet. Click "Generate & Publish Now" to start.</p>
          )}
        </div>

        {/* Activity Log */}
        <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Activity size={20} className="text-amber-400" /> Activity Log
          </h3>
          {status?.recent_logs?.length > 0 ? (
            <div className="space-y-2 max-h-80 overflow-y-auto">
              {status.recent_logs.map((log, i) => (
                <div key={log.id || i} className="flex items-start gap-3 p-2.5 rounded-lg hover:bg-slate-700/20 transition-colors" data-testid={`engine-log-${i}`}>
                  <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${
                    log.action === 'auto_published' ? 'bg-green-400' :
                    log.action === 'trend_published' ? 'bg-amber-400' :
                    log.action === 'engine_toggled' ? 'bg-amber-400' :
                    log.action === 'images_regenerated' ? 'bg-orange-400' :
                    log.action === 'error' ? 'bg-red-400' : 'bg-slate-400'
                  }`} />
                  <div className="flex-1 min-w-0">
                    <p className="text-slate-300 text-sm">
                      {log.action === 'auto_published' && `Published: "${log.blog_title}"`}
                      {log.action === 'trend_published' && `Trending: "${log.blog_title}"`}
                      {log.action === 'trend_detected' && `Trend detected: "${log.topic}" — awaiting review`}
                      {log.action === 'engine_toggled' && `Engine ${log.enabled ? 'enabled' : 'disabled'}`}
                      {log.action === 'generation_failed' && `Generation failed for: "${log.topic}"`}
                      {log.action === 'images_regenerated' && `Regenerated ${log.count} blog images`}
                      {log.action === 'error' && `Error: ${log.error?.substring(0, 80)}`}
                    </p>
                    <p className="text-slate-500 text-xs mt-0.5">{formatDate(log.timestamp)}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-slate-500 text-sm">No activity yet.</p>
          )}
        </div>
      </div>

      {/* All Auto-Generated Blogs with Social Snippets */}
      {autoBlogs.length > 0 && (
        <div className="mt-6 bg-slate-800/50 rounded-xl p-6 border border-slate-700" data-testid="auto-blogs-list">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <FileText size={20} className="text-orange-400" /> All Auto-Generated Blogs
            <span className="text-sm text-slate-400 font-normal ml-2">({autoBlogs.length} blogs)</span>
          </h3>
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {autoBlogs.map((blog, i) => (
              <div key={blog.id} className="flex items-center gap-3 p-3 bg-slate-700/30 rounded-lg hover:bg-slate-700/50 transition-colors" data-testid={`auto-blog-${i}`}>
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm font-medium truncate">{blog.title}</p>
                  <div className="flex items-center gap-2 text-xs text-slate-500 mt-0.5">
                    <span>{blog.category?.replace(/-/g, ' ')}</span>
                    <span>&middot;</span>
                    <span>{formatDate(blog.published_at)}</span>
                    {blog.is_trending && <span className="px-1.5 py-0.5 bg-amber-500/20 text-amber-400 rounded text-[10px]">TREND</span>}
                  </div>
                </div>
                <button
                  onClick={() => fetchSocialSnippets(blog.id, blog.title)}
                  className="p-2 text-slate-400 hover:text-orange-400 hover:bg-orange-500/10 rounded-lg transition-colors"
                  title="View Social Snippets"
                  data-testid={`snippet-btn-${i}`}
                >
                  <Share2 size={16} />
                </button>
                <button
                  onClick={() => window.open(`/blog/${blog.slug}`, '_blank')}
                  className="p-2 text-slate-400 hover:text-white hover:bg-slate-600 rounded-lg transition-colors"
                  title="View Blog"
                >
                  <Eye size={16} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Social Snippets Panel */}
      {selectedSnippets && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-6 bg-slate-800/50 rounded-xl p-6 border border-orange-500/30"
          data-testid="social-snippets-panel"
        >
          <div className="flex items-center justify-between mb-5">
            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
              <Share2 size={20} className="text-orange-400" /> Social Media Snippets
            </h3>
            <button onClick={() => setSelectedSnippets(null)} className="text-slate-400 hover:text-white text-sm">Close</button>
          </div>
          <p className="text-slate-400 text-sm mb-4">Blog: <span className="text-white">{selectedSnippets.title}</span></p>

          {Object.keys(selectedSnippets.social_snippets || {}).length === 0 ? (
            <p className="text-slate-500 text-sm">No social snippets available for this blog. Snippets are auto-generated for new blogs.</p>
          ) : (
            <div className="space-y-4">
              {[
                { key: 'linkedin', label: 'LinkedIn', color: 'orange' },
                { key: 'twitter', label: 'Twitter / X', color: 'amber' },
                { key: 'instagram', label: 'Instagram', color: 'rose' }
              ].map(({ key, label, color }) => {
                const snippet = selectedSnippets.social_snippets?.[key];
                if (!snippet) return null;
                const colorMap = {
                  orange: 'border-orange-500/30 bg-orange-500/5',
                  amber: 'border-amber-500/30 bg-amber-500/5',
                  rose: 'border-rose-500/30 bg-rose-500/5'
                };
                const btnMap = {
                  orange: 'bg-orange-500/20 text-orange-400 hover:bg-orange-500/30',
                  amber: 'bg-amber-500/20 text-amber-400 hover:bg-amber-500/30',
                  rose: 'bg-rose-500/20 text-rose-400 hover:bg-rose-500/30'
                };
                return (
                  <div key={key} className={`p-4 rounded-xl border ${colorMap[color]}`} data-testid={`snippet-${key}`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-white font-medium text-sm">{label}</span>
                      <button
                        onClick={() => copyToClipboard(snippet, label)}
                        className={`flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-medium transition-colors ${btnMap[color]}`}
                        data-testid={`copy-${key}-btn`}
                      >
                        <Code size={12} /> Copy
                      </button>
                    </div>
                    <p className="text-slate-300 text-sm whitespace-pre-wrap leading-relaxed">{snippet}</p>
                  </div>
                );
              })}
            </div>
          )}
        </motion.div>
      )}
    </div>
  );
};



// Analytics Dashboard View
const AnalyticsView = ({ cmsPin }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState(false);

  useEffect(() => {
    fetch(`${API_URL}/api/content-engine/analytics`, {
      headers: { 'X-CMS-Pin': cmsPin }
    }).then(r => r.json()).then(setData).catch(console.error).finally(() => setLoading(false));
  }, [cmsPin]);

  const handleRegenerateImages = async () => {
    setRegenerating(true);
    toast.info('Generating AI images for blogs without images... This may take a few minutes.');
    try {
      const res = await fetch(`${API_URL}/api/content-engine/regenerate-images`, {
        method: 'POST',
        headers: { 'X-CMS-Pin': cmsPin }
      });
      const result = await res.json();
      toast.success(result.message);
    } catch (err) {
      toast.error('Failed to regenerate images');
    } finally {
      setRegenerating(false);
    }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><RefreshCw className="animate-spin text-orange-400" size={32} /></div>;
  if (!data) return <div className="text-slate-400 text-center py-20">Failed to load analytics</div>;

  const maxViews = Math.max(...(data.categories || []).map(c => c.views), 1);

  return (
    <div data-testid="analytics-view">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-3">
            <TrendingUp className="text-orange-400" size={28} /> Blog Analytics
          </h2>
          <p className="text-slate-400 mt-1">Track performance across all your content</p>
        </div>
        <button onClick={handleRegenerateImages} disabled={regenerating} data-testid="regenerate-images-btn"
          className="flex items-center gap-2 px-4 py-2.5 bg-amber-500/20 text-amber-400 border border-amber-500/30 rounded-xl hover:bg-amber-500/30 transition-all disabled:opacity-50">
          <Sparkles size={18} /> {regenerating ? 'Generating...' : 'Regenerate Missing Images'}
        </button>
      </div>

      {/* Overview Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <div className="bg-slate-800/50 rounded-xl p-5 border border-slate-700">
          <p className="text-slate-400 text-sm mb-1">Total Published</p>
          <p className="text-3xl font-bold text-white" data-testid="total-blogs">{data.overview.total_blogs}</p>
        </div>
        <div className="bg-slate-800/50 rounded-xl p-5 border border-slate-700">
          <p className="text-slate-400 text-sm mb-1">Auto-Generated</p>
          <p className="text-3xl font-bold text-orange-400" data-testid="total-auto">{data.overview.total_auto}</p>
        </div>
        <div className="bg-slate-800/50 rounded-xl p-5 border border-slate-700">
          <p className="text-slate-400 text-sm mb-1">Manual Content</p>
          <p className="text-3xl font-bold text-amber-400">{data.overview.total_manual}</p>
        </div>
        <div className="bg-slate-800/50 rounded-xl p-5 border border-slate-700">
          <p className="text-slate-400 text-sm mb-1">Total Views</p>
          <p className="text-3xl font-bold text-orange-400" data-testid="total-views">{data.overview.total_views}</p>
        </div>
      </div>

      {/* Two column: Category Performance + Top Posts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Category Performance */}
        <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
          <h3 className="text-lg font-semibold text-white mb-5 flex items-center gap-2">
            <FolderOpen size={20} className="text-orange-400" /> Category Performance
          </h3>
          <div className="space-y-4">
            {(data.categories || []).map((cat, i) => (
              <div key={cat.category} data-testid={`cat-stat-${i}`}>
                <div className="flex justify-between text-sm mb-1.5">
                  <span className="text-white font-medium">{cat.name}</span>
                  <span className="text-slate-400">{cat.count} posts &middot; {cat.views} views</span>
                </div>
                <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${(cat.views / maxViews) * 100}%` }}
                    transition={{ duration: 0.8, delay: i * 0.1 }}
                    className="h-full bg-gradient-to-r from-orange-500 to-amber-500 rounded-full"
                  />
                </div>
                <p className="text-xs text-slate-500 mt-1">Avg: {cat.avg_views} views/post</p>
              </div>
            ))}
          </div>
        </div>

        {/* Top 10 Posts */}
        <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
          <h3 className="text-lg font-semibold text-white mb-5 flex items-center gap-2">
            <BarChart3 size={20} className="text-orange-400" /> Top Performing Posts
          </h3>
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {(data.top_posts || []).map((post, i) => (
              <div key={post.id} className="flex items-center gap-3 p-3 bg-slate-700/30 rounded-lg" data-testid={`top-post-${i}`}>
                <span className={`font-bold text-sm w-7 text-center ${i < 3 ? 'text-orange-400' : 'text-slate-500'}`}>#{i + 1}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm font-medium truncate">{post.title}</p>
                  <div className="flex items-center gap-2 text-xs text-slate-500 mt-0.5">
                    <span>{post.category?.replace(/-/g, ' ')}</span>
                    {post.source === 'content-engine' && <span className="px-1.5 py-0.5 bg-orange-500/20 text-orange-400 rounded text-[10px]">AUTO</span>}
                  </div>
                </div>
                <div className="flex items-center gap-1 text-orange-400 text-sm font-medium">
                  <Eye size={14} /> {post.views || 0}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Tag Performance + Source Comparison */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Tag Performance */}
        <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
          <h3 className="text-lg font-semibold text-white mb-5 flex items-center gap-2">
            <Tag size={20} className="text-amber-400" /> Tag Performance
          </h3>
          <div className="flex flex-wrap gap-2">
            {(data.tags || []).map((tag, i) => (
              <div key={tag.tag} className="px-3 py-2 bg-slate-700/50 rounded-lg border border-slate-600/50" data-testid={`tag-stat-${i}`}>
                <span className="text-white text-sm">#{tag.tag}</span>
                <div className="text-xs text-slate-400 mt-0.5">{tag.count} posts &middot; {tag.views} views</div>
              </div>
            ))}
            {(data.tags || []).length === 0 && <p className="text-slate-500 text-sm">No tag data yet</p>}
          </div>
        </div>

        {/* Content Source Comparison */}
        <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
          <h3 className="text-lg font-semibold text-white mb-5 flex items-center gap-2">
            <Zap size={20} className="text-amber-400" /> Auto vs Manual Content
          </h3>
          <div className="space-y-6">
            {Object.entries(data.views_by_source || {}).map(([source, stats]) => (
              <div key={source}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-white font-medium flex items-center gap-2">
                    {source === 'content-engine' ? <><Zap size={16} className="text-orange-400" /> Auto Engine</> : <><Edit size={16} className="text-amber-400" /> Manual</>}
                  </span>
                  <span className="text-slate-400 text-sm">{stats.count} posts</span>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-slate-700/30 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-white">{stats.views}</p>
                    <p className="text-xs text-slate-400">Total Views</p>
                  </div>
                  <div className="bg-slate-700/30 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-white">{stats.count > 0 ? Math.round(stats.views / stats.count) : 0}</p>
                    <p className="text-xs text-slate-400">Avg Views/Post</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};


// Main CMS Component
const CMS = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [cmsPin, setCmsPin] = useState('');
  const [activeTab, setActiveTab] = useState('dashboard');
  const [stats, setStats] = useState({
    total_blogs: 0,
    published_blogs: 0,
    draft_blogs: 0,
    scheduled_blogs: 0,
    total_views: 0,
    total_authors: 0,
    total_categories: 0,
    total_clusters: 0
  });
  const [loading, setLoading] = useState(true);
  const [showEditor, setShowEditor] = useState(false);
  const [editingBlog, setEditingBlog] = useState(null);

  useEffect(() => {
    const storedPin = sessionStorage.getItem('cmsPin');
    if (storedPin) {
      verifyStoredPin(storedPin);
    } else {
      setLoading(false);
    }
  }, []);

  const verifyStoredPin = async (pin) => {
    try {
      const response = await fetch(`${API_URL}/api/cms/verify-pin`, {
        method: 'POST',
        headers: { 'X-CMS-Pin': pin }
      });
      const data = await response.json();
      
      if (data.valid) {
        setCmsPin(pin);
        setIsAuthenticated(true);
        fetchStats(pin);
      } else {
        sessionStorage.removeItem('cmsPin');
      }
    } catch (err) {
      sessionStorage.removeItem('cmsPin');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async (pin) => {
    try {
      const response = await fetch(`${API_URL}/api/cms/stats`, {
        headers: { 'X-CMS-Pin': pin }
      });
      const data = await response.json();
      setStats(data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const handleLoginSuccess = (pin) => {
    setCmsPin(pin);
    setIsAuthenticated(true);
    fetchStats(pin);
  };

  const handleLogout = () => {
    sessionStorage.removeItem('cmsPin');
    setIsAuthenticated(false);
    setCmsPin('');
  };

  const handleCreateOrEditBlog = (blog = null) => {
    setEditingBlog(blog);
    setShowEditor(true);
  };

  const handleEditorClose = () => {
    setShowEditor(false);
    setEditingBlog(null);
  };

  const handleEditorSave = () => {
    setShowEditor(false);
    setEditingBlog(null);
    fetchStats(cmsPin);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <motion.div
          className="w-12 h-12 border-2 border-orange-500/30 border-t-orange-500 rounded-full"
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
        />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <CMSLogin onSuccess={handleLoginSuccess} />;
  }

  if (showEditor) {
    return (
      <BlogEditor 
        blog={editingBlog}
        cmsPin={cmsPin}
        onClose={handleEditorClose}
        onSave={handleEditorSave}
      />
    );
  }

  const renderContent = () => {
    switch (activeTab) {
      case 'dashboard':
        return <DashboardView stats={stats} cmsPin={cmsPin} />;
      case 'blogs':
        return <BlogsView cmsPin={cmsPin} onCreateNew={handleCreateOrEditBlog} />;
      case 'authors':
        return <AuthorsView cmsPin={cmsPin} />;
      case 'categories':
        return <CategoriesView cmsPin={cmsPin} />;
      case 'tags':
        return <TagsView cmsPin={cmsPin} />;
      case 'clusters':
        return <ClustersView cmsPin={cmsPin} />;
      case 'engine':
        return <ContentEngineView cmsPin={cmsPin} />;
      case 'analytics':
        return <AnalyticsView cmsPin={cmsPin} />;
      default:
        return <DashboardView stats={stats} cmsPin={cmsPin} />;
    }
  };

  return (
    <div className="min-h-screen bg-slate-900 flex">
      <Sidebar activeTab={activeTab} setActiveTab={setActiveTab} onLogout={handleLogout} />
      
      <main className="flex-1 p-8">
        {renderContent()}
      </main>
    </div>
  );
};

export default CMS;
